import { Candidate } from "@/types/candidate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SkillsEditor } from "./SkillsEditor";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";

interface EditCandidateFormFieldsProps {
  formData: Partial<Candidate>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleStatusChange: (value: string) => void;
  handleSkillsChange: (skills: string[]) => void;
}

export const EditCandidateFormFields = ({
  formData,
  handleChange,
  handleStatusChange,
  handleSkillsChange
}: EditCandidateFormFieldsProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">{t("candidates.newCandidateModal.nameLabel")}</Label>
        <Input 
          id="name"
          name="name"
          value={formData.name || ""}
          onChange={handleChange}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">{t("candidates.newCandidateModal.emailLabel")}</Label>
        <Input 
          id="email"
          name="email"
          type="email"
          value={formData.email || ""}
          onChange={handleChange}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phone">{t("candidates.profile.phone")}</Label>
        <Input 
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone || ""}
          onChange={handleChange}
          placeholder="123-456-7890"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="linkedin">{t("candidates.profile.linkedin")}</Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Linkedin className="h-4 w-4 text-gray-500" />
          </div>
          <Input 
            id="linkedin"
            name="linkedin"
            type="text"
            value={formData.linkedin || ""}
            onChange={handleChange}
            placeholder="https://linkedin.com/in/username"
            className="pl-10"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="function">{t("candidates.newCandidateModal.functionLabel")}</Label>
        <Input 
          id="function"
          name="function"
          value={formData.function || ""}
          onChange={handleChange}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="manager">{t("companies.info.assignee", "Manager")}</Label>
        <Input 
          id="manager"
          name="manager"
          value={formData.manager || ""}
          onChange={handleChange}
          placeholder={t("companies.info.assignee", "Recruiter in charge")}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="status">{t("candidates.list.status")}</Label>
        <Select 
          value={formData.status || "Looking"} 
          onValueChange={handleStatusChange}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("candidates.list.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Looking">{t("candidates.filters.status.looking")}</SelectItem>
            <SelectItem value="Open">{t("candidates.filters.status.open")}</SelectItem>
            <SelectItem value="Unavailable">{t("candidates.filters.status.unavailable")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <SkillsEditor 
        skills={formData.skills || []} 
        onChange={handleSkillsChange} 
      />
    </div>
  );
};
